import { useSelector } from 'react-redux'
import { Typography } from '~/components/common/Typography'
import {
  selectFlowRepository,
  selectSelectedFlows,
  selectAutoGenerateTestData,
  selectDeploymentConfig,
  selectApplicationSchemas
} from '~/store/integrationTestFlowSlice'

// Checkmark icon SVG - filled circle with checkmark (from Figma design)
const CheckmarkIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 14 14'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='h-[16px] w-[16px]'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667ZM10.1381 4.80474L9.19526 3.86193L5.33333 7.72386L3.80474 6.19526L2.86193 7.13807L5.33333 9.60948L10.1381 4.80474Z'
      fill='#65558F'
    />
  </svg>
)

interface ConfirmationItem {
  title: string
  description: string
}

export function ConfirmationSlide() {
  const flowRepository = useSelector(selectFlowRepository)
  const selectedFlows = useSelector(selectSelectedFlows)
  const autoGenerateTestData = useSelector(selectAutoGenerateTestData)
  const deploymentConfig = useSelector(selectDeploymentConfig)
  const applicationSchemas = useSelector(selectApplicationSchemas)

  // Calculate data set and schema counts
  const flowsWithData = selectedFlows.filter(f => f.selected)
  const totalDataSets = flowsWithData.reduce((acc, flow) => acc + (flow.examples?.length || 0), 0)
  const totalSchemas = applicationSchemas.reduce((acc, app) => acc + app.schemas.length, 0)

  // Determine deployment description based on option
  const getDeploymentDescription = () => {
    if (!deploymentConfig) {
      return 'Not configured'
    }

    if (deploymentConfig.deploymentOption === 'agent') {
      // Show VM args if provided, otherwise show "VM args is not provided"
      const vmArgs = deploymentConfig.vmArgs
      if (vmArgs && vmArgs.trim() !== '') {
        return vmArgs
      } else {
        return 'VM args is not provided'
      }
    } else {
      // Show deployment URL for existing deployment
      return deploymentConfig.deploymentUrl || 'Deployment URL not provided'
    }
  }

  // Determine if this is an S3 upload or GitHub repo
  const isS3Upload = flowRepository?.conn_type === 'S3' || Boolean(flowRepository?.s3TarFilePath)
  const s3Path = flowRepository?.s3TarFilePath || ''

  // Extract folder name from S3 path for display
  const displayRepoName =
    isS3Upload && s3Path
      ? (() => {
          const filename =
            s3Path
              .split('/')
              .pop()
              ?.replace(/\.tar(\.gz)?$/, '') || 'Uploaded Project'
          const parts = filename.split('___')
          return parts.length > 1 ? parts[1] : filename
        })()
      : flowRepository
        ? `${flowRepository.repository}/${flowRepository.branch}`
        : 'Not selected'

  const items: ConfirmationItem[] = [
    {
      title: isS3Upload ? 'Uploaded folder' : 'Repository & Branch',
      description: displayRepoName
    },
    {
      title: 'Choose & configure flow(s)',
      description: `${flowsWithData.length} flows selected; ${totalDataSets} data set provided; ${totalSchemas} schema provided`
    },
    {
      title: 'Deployment',
      description: getDeploymentDescription()
    }
  ]

  return (
    <div className='flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden px-[24px] pb-[436px] pt-[70px]'>
      <div className='flex w-full max-w-[520px] flex-col gap-[48px]'>
        {/* Title Section */}
        <div className='flex w-full flex-col items-center gap-[60px]'>
          <div className='flex w-full flex-col items-center gap-[16px]'>
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-center text-black'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
            >
              Confirmation
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-center text-[rgba(0,0,0,0.65)]'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              Here's your a summary of your configuration, please confirm and proceed to submit. You
              can start over if needed.
            </Typography>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex w-full flex-col gap-[48px]'>
          <div className='flex w-full flex-col gap-[8px]'>
            {/* Summary Card */}
            <div className='flex w-full flex-col gap-[12px] rounded-[24px] border border-[rgba(0,0,0,0.02)] bg-[rgba(189,189,189,0.08)] p-[24px]'>
              {items.map((item, index) => (
                <div
                  key={index}
                  className={`flex h-[48px] w-full flex-col gap-[24px] px-0 py-[4px] ${
                    index < items.length - 1 ? 'border-b border-[#EAEAEA]' : ''
                  }`}
                >
                  <div className='flex w-full items-center gap-[8px] pl-[12px] pr-0'>
                    {/* Checkmark Icon Container */}
                    <div className='flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[12px]'>
                      <CheckmarkIcon />
                    </div>

                    {/* Text Content */}
                    <div className='flex flex-1 min-w-0 flex-col gap-[4px]'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodyMd'
                        fontWeight='bold'
                        className='text-[rgba(0,0,0,0.88)]'
                        style={{ fontSize: '14px', fontWeight: 700, lineHeight: '18px' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className={`text-[rgba(0,0,0,0.45)] ${
                          index === items.length - 1
                            ? 'whitespace-pre'
                            : 'overflow-hidden text-ellipsis whitespace-nowrap'
                        }`}
                        style={{ fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}
                      >
                        {item.description}
                      </Typography>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmationSlide
